"use client"

import { useState, useEffect, useCallback } from "react"
import { preloadHeroSprites, preloadArcherSprites, preloadMageSprites, preloadSiegeSprites, preloadTowerSprites, preloadSummonSprites, preloadEnemySprites, preloadTilesetImages } from "@/utils/rendering"
import { itemDefinitions } from "@/data/item-types"

// Pre-compute item images at module load time (only happens once)
const ITEM_IMAGES: string[] = (() => {
  const images = new Set<string>()
  for (const item of Object.values(itemDefinitions)) {
    if (item.image) images.add(item.image)
  }
  return Array.from(images)
})()

// ============================================================================
// PHASE 1: Title Screen Assets (~2MB, ~10 files)
// ============================================================================
const PHASE_1_ASSETS = {
  screens: ["/images/mainscreen-bg.png", "/images/pelf-logo.png"],
  ui: ["/images/btn-9slice.png", "/images/l-pillarbox.png", "/images/r-pillarbox.png"],
  sounds: ["/sounds/events/menu.ogg"],
}

// ============================================================================
// PHASE 2: Droppod & Hero Select Assets (~1MB, ~9 files)
// ============================================================================
const PHASE_2_ASSETS = {
  minigame: ["/images/droppod.png", "/images/droppod-landed.png", "/images/droppod-empty.png", "/images/droppod-clone.png"],
  portraits: ["/images/heroes/portraits/portrait-paladin.png", "/images/heroes/portraits/portrait-ranger.png", "/images/heroes/portraits/portrait-siegesmith.png"],
  ui: ["/images/bottom-bar-droppod-bg.png"],
  sounds: ["/sounds/droppod/droppod-fly.mp3", "/sounds/droppod/droppod-land.mp3"],
}

// ============================================================================
// HERO ASSETS: Per-hero sprite sheets (loaded based on selection)
// ============================================================================
const HERO_ASSETS: Record<string, string[]> = {
  paladin: [
    "/images/heroes/Paladin_Walk.png",
    "/images/heroes/Paladin_Attack 1.png",
    "/images/heroes/Paladin_Attack 2.png",
    "/images/heroes/Paladin_Attack 3.png",
    "/images/heroes/Paladin_Hurt.png",
    "/images/heroes/Paladin_Dead.png",
    "/images/heroes/Paladin_Idle_2.png",
    "/images/heroes/Paladin_Shield_Strike.png",
    "/images/heroes/Paladin_Invocation.png",
  ],
  ranger: [
    "/images/heroes/Ranger_Walk.png",
    "/images/heroes/Ranger_Attack.png",
    "/images/heroes/Ranger_Hurt.png",
    "/images/heroes/Ranger_Dead.png",
    "/images/heroes/Ranger_Idle.png",
  ],
  siegesmith: [
    "/images/heroes/Siegesmith_Walk.png",
    "/images/heroes/Siegesmith_Attack_1.png",
    "/images/heroes/Siegesmith_Attack_2.png",
    "/images/heroes/Siegesmith_Hurt.png",
    "/images/heroes/Siegesmith_Dead.png",
    "/images/heroes/Siegesmith_Idle.png",
    "/images/heroes/Siegesmith_Idle_2.png",
    "/images/heroes/Siegesmith_Jump.png",
    "/images/heroes/Siegesmith_Special.png",
  ],
}

// ============================================================================
// TOWER & SUMMON ASSETS: All tower and summon sprites (Phase 3)
// ============================================================================
const TOWER_AND_SUMMON_ASSETS = {
  towers: ["/images/mage-tower.png", "/images/mage.png", "/images/towers/archer/icon.png", "/images/towers/mage/icon.png"],
  towerSpritesheets: [
    // Archer tower - upgrade/build animations
    "/images/towers/archer/1 Upgrade/1.png",
    "/images/towers/archer/1 Upgrade/2.png",
    "/images/towers/archer/1 Upgrade/3.png",
    "/images/towers/archer/1 Upgrade/4.png",
    "/images/towers/archer/1 Upgrade/5.png",
    "/images/towers/archer/1 Upgrade/6.png",
    "/images/towers/archer/1 Upgrade/7.png",
    // Archer tower - idle sprites per level
    "/images/towers/archer/2 Idle/1.png",
    "/images/towers/archer/2 Idle/2.png",
    "/images/towers/archer/2 Idle/3.png",
    "/images/towers/archer/2 Idle/4.png",
    "/images/towers/archer/2 Idle/5.png",
    "/images/towers/archer/2 Idle/6.png",
    "/images/towers/archer/2 Idle/7.png",
    // Archer unit sprites (animated archer on tower)
    "/images/towers/archer/3 Units/3/D_Idle.png",
    "/images/towers/archer/3 Units/3/D_Preattack.png",
    "/images/towers/archer/3 Units/3/D_Attack.png",
    "/images/towers/archer/3 Units/3/S_Idle.png",
    "/images/towers/archer/3 Units/3/S_Preattack.png",
    "/images/towers/archer/3 Units/3/S_Attack.png",
    "/images/towers/archer/3 Units/3/U_Idle.png",
    "/images/towers/archer/3 Units/3/U_Preattack.png",
    "/images/towers/archer/3 Units/3/U_Attack.png",
    // Arrow projectile sprites (27 rotation angles)
    "/images/towers/archer/3 Units/Arrow/1.png",
    "/images/towers/archer/3 Units/Arrow/2.png",
    "/images/towers/archer/3 Units/Arrow/3.png",
    "/images/towers/archer/3 Units/Arrow/4.png",
    "/images/towers/archer/3 Units/Arrow/5.png",
    "/images/towers/archer/3 Units/Arrow/6.png",
    "/images/towers/archer/3 Units/Arrow/7.png",
    "/images/towers/archer/3 Units/Arrow/8.png",
    "/images/towers/archer/3 Units/Arrow/9.png",
    "/images/towers/archer/3 Units/Arrow/10.png",
    "/images/towers/archer/3 Units/Arrow/11.png",
    "/images/towers/archer/3 Units/Arrow/12.png",
    "/images/towers/archer/3 Units/Arrow/13.png",
    "/images/towers/archer/3 Units/Arrow/14.png",
    "/images/towers/archer/3 Units/Arrow/15.png",
    "/images/towers/archer/3 Units/Arrow/16.png",
    "/images/towers/archer/3 Units/Arrow/17.png",
    "/images/towers/archer/3 Units/Arrow/18.png",
    "/images/towers/archer/3 Units/Arrow/19.png",
    "/images/towers/archer/3 Units/Arrow/20.png",
    "/images/towers/archer/3 Units/Arrow/21.png",
    "/images/towers/archer/3 Units/Arrow/22.png",
    "/images/towers/archer/3 Units/Arrow/23.png",
    "/images/towers/archer/3 Units/Arrow/24.png",
    "/images/towers/archer/3 Units/Arrow/25.png",
    "/images/towers/archer/3 Units/Arrow/26.png",
    "/images/towers/archer/3 Units/Arrow/27.png",
    // Mage tower - upgrade/build animations
    "/images/towers/mage/1 Upgrade/1.png",
    "/images/towers/mage/1 Upgrade/2.png",
    "/images/towers/mage/1 Upgrade/3.png",
    "/images/towers/mage/1 Upgrade/4.png",
    "/images/towers/mage/1 Upgrade/5.png",
    "/images/towers/mage/1 Upgrade/6.png",
    "/images/towers/mage/1 Upgrade/7.png",
    // Mage tower - idle sprites per level
    "/images/towers/mage/2 Idle/1.png",
    "/images/towers/mage/2 Idle/2.png",
    "/images/towers/mage/2 Idle/3.png",
    "/images/towers/mage/2 Idle/4.png",
    "/images/towers/mage/2 Idle/5.png",
    "/images/towers/mage/2 Idle/6.png",
    "/images/towers/mage/2 Idle/7.png",
    // Mage unit sprites (animated mage on tower)
    "/images/towers/mage/3 Units/3/D_Idle.png",
    "/images/towers/mage/3 Units/3/D_Attack.png",
    "/images/towers/mage/3 Units/3/S_Idle.png",
    "/images/towers/mage/3 Units/3/S_Attack.png",
    "/images/towers/mage/3 Units/3/U_Idle.png",
    "/images/towers/mage/3 Units/3/U_Attack.png",
    // Mage projectile sprite
    "/images/towers/mage/3 Units/Projectiles/3.png",
    // Siege tower - upgrade/build animations
    "/images/towers/siege/1 Upgrade/1.png",
    "/images/towers/siege/1 Upgrade/2.png",
    "/images/towers/siege/1 Upgrade/3.png",
    "/images/towers/siege/1 Upgrade/4.png",
    "/images/towers/siege/1 Upgrade/5.png",
    "/images/towers/siege/1 Upgrade/6.png",
    "/images/towers/siege/1 Upgrade/7.png",
    // Siege tower - idle sprites per level
    "/images/towers/siege/2 Idle/1.png",
    "/images/towers/siege/2 Idle/2.png",
    "/images/towers/siege/2 Idle/3.png",
    "/images/towers/siege/2 Idle/4.png",
    "/images/towers/siege/2 Idle/5.png",
    "/images/towers/siege/2 Idle/6.png",
    "/images/towers/siege/2 Idle/7.png",
    // Siege catapult unit sprites
    "/images/towers/siege/3 Units/3/D.png",
    "/images/towers/siege/3 Units/3/DS.png",
    "/images/towers/siege/3 Units/3/S.png",
    "/images/towers/siege/3 Units/3/U.png",
    "/images/towers/siege/3 Units/3/US.png",
    // Siege projectile sprite
    "/images/towers/siege/3 Units/Projectiles/3.png",
    // Siege tower icon
    "/images/towers/siege/icon.png",
    // Barracks tower - upgrade/build animations
    "/images/towers/barracks/1 Upgrade/1.png",
    "/images/towers/barracks/1 Upgrade/2.png",
    "/images/towers/barracks/1 Upgrade/3.png",
    "/images/towers/barracks/1 Upgrade/4.png",
    "/images/towers/barracks/1 Upgrade/5.png",
    "/images/towers/barracks/1 Upgrade/6.png",
    "/images/towers/barracks/1 Upgrade/7.png",
    // Barracks tower - idle sprites per level
    "/images/towers/barracks/2 Idle/1.png",
    "/images/towers/barracks/2 Idle/2.png",
    "/images/towers/barracks/2 Idle/3.png",
    "/images/towers/barracks/2 Idle/4.png",
    "/images/towers/barracks/2 Idle/5.png",
    "/images/towers/barracks/2 Idle/6.png",
    "/images/towers/barracks/2 Idle/7.png",
    // Turret summon sprites
    "/images/summons/Turret_Idle.png",
    "/images/summons/Turret_Attack.png",
    "/images/summons/Turret_Hurt.png",
    "/images/summons/Turret_Bullet.png",
    // Fox summon sprites
    "/images/summons/Fox_Idle_with_shadow.png",
    "/images/summons/Fox_walk_with_shadow.png",
    "/images/summons/Fox_Run_with_shadow.png",
    "/images/summons/Fox_Hurt_with_shadow.png",
    "/images/summons/Fox_Death_with_shadow.png",
    // Boar summon sprites
    "/images/summons/Boar_Idle_with_shadow.png",
    "/images/summons/Boar_Walk_with_shadow.png",
    "/images/summons/Boar_Run_with_shadow.png",
    "/images/summons/Boar_Attack_with_shadow.png",
    "/images/summons/Boar_Hurt_with_shadow.png",
    "/images/summons/Boar_Death_with_shadow.png",
  ],
}

// ============================================================================
// LEVEL ASSETS: Per-level tilesets, enemies, and music
// ============================================================================
const LEVEL_ASSETS: Record<number, { tilesets: string[]; enemies: string[]; sounds: string[] }> = {
  1: {
    tilesets: [
      "/images/biomes/grassland/Details.png",
      "/images/biomes/grassland/Trees_rocks.png",
      "/images/biomes/grassland/Water_coasts.png",
      "/images/biomes/grassland/ground_grasss.png",
      "/images/biomes/grassland/water_detilazation.png",
      "/images/biomes/grassland/water_detilazation_v2.png",
    ],
    enemies: ["/images/enemy1.png"],
    sounds: ["/sounds/levels/pelf_level1.ogg"],
  },
  // Future levels can be added here
}

// ============================================================================
// SHARED ASSETS: Used throughout the game (UI, shop, starmap, items)
// ============================================================================
const SHARED_ASSETS = {
  ui: [
    "/images/bottom-bar-bg.png",
    "/images/bottom-bar-shop-bg.png",
    "/images/radialmenu.png",
    "/images/icon-coins.png",
    "/images/icon-lives.png",
    "/images/icon-waves.png",
    "/images/icon-premiumtower.png",
    "/images/armor-hero.png",
    "/images/damage-hero.png",
    "/images/towers/barracks/portraits/soldiers.png",
  ],
  screens: [
    "/images/logo-victory.png",
    "/images/logo-defeated.png",
    "/images/shop-bg.png",
    "/images/shop-bg2.png",
    "/images/shop-window-bg.png",
    "/images/shop-front.png",
    "/images/shop-front-2.png",
    "/images/shop-front-3.png",
    "/images/shop-front-4.png",
    "/images/billboard-front.png",
    "/images/shopkeep-1.png",
    "/images/shopkeep-2.png",
    "/images/shopkeep-3.png",
    "/images/shopkeep-4.png",
    "/images/shopkeep-1-character.png",
    "/images/shopkeep-2-character.png",
    "/images/shopkeep-3-character.png",
    "/images/shopkeep-4-character.png",
    "/images/starmap/starmap.png",
    "/images/starmap/starmap_node_boss.png",
    "/images/starmap/starmap_node_combat.png",
    "/images/starmap/starmap_node_specialevent.png",
    "/images/starmap/starmap_node_shop.png",
    "/images/starmap/starmap_node_pelf.png",
  ],
  sounds: [
    "/sounds/towers/archertower-fire1.mp3",
    "/sounds/towers/archertower-fire2.mp3",
    "/sounds/towers/archertower-hit1.mp3",
    "/sounds/towers/archertower-hit2.mp3",
    "/sounds/towers/siegetower-fire.mp3",
    "/sounds/towers/siegetower-hit.mp3",
    "/sounds/towers/magetower-fire.mp3",
    "/sounds/towers/tower-build.mp3",
    "/sounds/towers/tower-sell.mp3",
    "/sounds/events/level-victory.mp3",
    "/sounds/events/defeat.mp3",
    "/sounds/events/newwave.mp3",
    "/sounds/events/shop.mp3",
    "/sounds/events/starmap.mp3",
    "/sounds/heroes/siegesmith-buildturret.mp3",
    "/sounds/heroes/siegesmith_turret_attack.mp3",
    "/sounds/items/inventory_potion.mp3",
  ],
  items: ITEM_IMAGES,
}

// ============================================================================
// BACKWARD COMPATIBILITY: Monolithic asset list (for existing loader)
// ============================================================================
const GAME_ASSETS = {
  heroes: ["/images/hero1.png", "/images/hero2.png", "/images/hero3.png"],
  portraits: PHASE_2_ASSETS.portraits,
  heroSpritesheets: [...HERO_ASSETS.paladin, ...HERO_ASSETS.ranger, ...HERO_ASSETS.siegesmith],
  towers: TOWER_AND_SUMMON_ASSETS.towers,
  towerSpritesheets: TOWER_AND_SUMMON_ASSETS.towerSpritesheets,
  enemies: LEVEL_ASSETS[1].enemies,
  ui: [...PHASE_1_ASSETS.ui, ...PHASE_2_ASSETS.ui, ...SHARED_ASSETS.ui],
  minigame: PHASE_2_ASSETS.minigame,
  screens: [...PHASE_1_ASSETS.screens, ...SHARED_ASSETS.screens],
  sounds: [...PHASE_1_ASSETS.sounds, ...PHASE_2_ASSETS.sounds, ...LEVEL_ASSETS[1].sounds, ...SHARED_ASSETS.sounds],
  tilesets: LEVEL_ASSETS[1].tilesets,
  spritesheets: [] as string[],
  items: ITEM_IMAGES,
}

// Flatten all assets into a single array
const getAllAssets = (): string[] => {
  return Object.values(GAME_ASSETS).flat()
}

interface PreloadState {
  loaded: number
  total: number
  progress: number
  isComplete: boolean
  errors: string[]
}

export const useAssetPreloader = (startPreloading = true) => {
  const [state, setState] = useState<PreloadState>({
    loaded: 0,
    total: 0,
    progress: 0,
    isComplete: false,
    errors: [],
  })

  const preloadImage = useCallback((src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new window.Image()
      img.onload = () => resolve()
      img.onerror = () => reject(new Error(`Failed to load: ${src}`))
      img.src = src
    })
  }, [])

  const preloadAudio = useCallback((src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const audio = new Audio()
      // canplaythrough is not guaranteed to fire (iOS Safari defers media
      // buffering) - treat a timeout as loaded rather than hanging forever
      const timeout = setTimeout(() => resolve(), 8000)
      audio.oncanplaythrough = () => {
        clearTimeout(timeout)
        resolve()
      }
      audio.onerror = () => {
        clearTimeout(timeout)
        reject(new Error(`Failed to load audio: ${src}`))
      }
      audio.src = src
      audio.load()
    })
  }, [])

  useEffect(() => {
    if (!startPreloading) return

    const assets = getAllAssets()
    const total = assets.length

    if (total === 0) {
      setState({ loaded: 0, total: 0, progress: 100, isComplete: true, errors: [] })
      return
    }

    setState((prev) => ({ ...prev, total }))

    let loadedCount = 0
    const errors: string[] = []

    const isAudioFile = (src: string) => {
      return /\.(mp3|wav|ogg|m4a)$/i.test(src)
    }

    const loadAsset = async (src: string) => {
      try {
        if (isAudioFile(src)) {
          await preloadAudio(src)
        } else {
          await preloadImage(src)
        }
      } catch {
        errors.push(src)
        console.warn(`[v0] Failed to preload asset: ${src}`)
      } finally {
        loadedCount++
        setState((prev) => ({
          ...prev,
          loaded: loadedCount,
          progress: Math.round((loadedCount / total) * 100),
          isComplete: loadedCount === total,
          errors: [...errors],
        }))
      }
    }

    // Load all assets in parallel
    Promise.all(assets.map(loadAsset)).then(() => {
      // After main assets are loaded, preload sprites into the rendering cache
      preloadHeroSprites()
      preloadArcherSprites()
      preloadMageSprites()
      preloadSiegeSprites()
      preloadTowerSprites()
      preloadSummonSprites()
      preloadEnemySprites()
      preloadTilesetImages()
    })
  }, [startPreloading, preloadImage, preloadAudio])

  return state
}

// Export asset lists for manual preloading if needed
export const PRELOAD_ASSETS = GAME_ASSETS

// Export phase-specific asset manifests (for future phased loading)
export { PHASE_1_ASSETS, PHASE_2_ASSETS, HERO_ASSETS, TOWER_AND_SUMMON_ASSETS, LEVEL_ASSETS, SHARED_ASSETS }
